import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import timelineService from '../services/timelineService';

const Dashboard = () => {
  const [timelines, setTimelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showPublicWarningModal, setShowPublicWarningModal] = useState(false);
  const [newTimeline, setNewTimeline] = useState({
    title: '',
    description: '',
    startDate: {
      year: '',
      month: '',
      day: '',
      isBC: false
    },
    endDate: {
      year: '',
      month: '',
      day: '',
      isBC: false
    },
    backgroundColor: '#FFFFFF',
    backgroundImage: '',
    logoUrl: '',
    beginAt: 'firstStory',
    isPublic: false,
    coverImage: '',
  });

  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    if (user) {
      fetchTimelines();
    }
  }, [user]);

  const fetchTimelines = async () => {
    try {
      const data = await timelineService.getTimelines();
      setTimelines(data);
    } catch (error) {
      console.error('Error fetching timelines:', error);

      // Handle authentication errors
      if (error.response && error.response.status === 401) {
        setError('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        setTimeout(() => {
          logout();
          navigate('/giris');
        }, 2000);
      } else {
        const message = error.response?.data?.message || 'Zaman çizelgesi yüklenirken hata oluştu';
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTimeline = async (e) => {
    e.preventDefault();
    setError('');

    // Validate required fields
    if (!newTimeline.title) {
      setError('Başlık alanı zorunludur');
      return;
    }

    // Validate start date
    if (!newTimeline.startDate.year || newTimeline.startDate.month === '' || !newTimeline.startDate.day) {
      setError('Başlangıç tarihi alanlarının tamamı doldurulmalıdır');
      return;
    }

    // Validate end date
    if (!newTimeline.endDate.year || newTimeline.endDate.month === '' || !newTimeline.endDate.day) {
      setError('Bitiş tarihi alanlarının tamamı doldurulmalıdır');
      return;
    }

    try {
      const response = await timelineService.createTimeline({
        title: newTimeline.title,
        intro: newTimeline.description, // Using description as intro
        about: '',
        startDate: newTimeline.startDate,
        endDate: newTimeline.endDate,
        backgroundColor: newTimeline.backgroundColor,
        backgroundImage: newTimeline.backgroundImage,
        logoUrl: newTimeline.logoUrl,
        beginAt: newTimeline.beginAt,
        isPublic: newTimeline.isPublic,
        coverImage: newTimeline.coverImage,
      });

      setTimelines([...timelines, response]);
      setShowCreateForm(false);
      setNewTimeline({
        title: '',
        description: '',
        startDate: {
          year: '',
          month: '',
          day: '',
          isBC: false
        },
        endDate: {
          year: '',
          month: '',
          day: '',
          isBC: false
        },
        backgroundColor: '#FFFFFF',
        backgroundImage: '',
        logoUrl: '',
        beginAt: 'firstStory',
        isPublic: false,
        coverImage: '',
      });
    } catch (error) {
      console.error('Error creating timeline:', error);

      // Handle authentication errors
      if (error.response && error.response.status === 401) {
        setError('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      } else {
        const message = error.response?.data?.message || 'Zaman çizelgesi oluşturulamadı';
        setError(message);
      }
    }
  };

  const handleDeleteTimeline = async (timelineId) => {
    if (!window.confirm('Bu zaman çizelgesini silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await timelineService.deleteTimeline(timelineId);
      setTimelines(timelines.filter(timeline => timeline._id !== timelineId));
    } catch (error) {
      console.error('Error deleting timeline:', error);

      // Handle authentication errors
      if (error.response && error.response.status === 401) {
        setError('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      } else {
        setError('Zaman çizelgesi silinemedi');
      }
    }
  };

  // File upload handlers
  const handleFileUpload = (field) => (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setNewTimeline({
          ...newTimeline,
          [field]: event.target.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="flex flex-col items-center text-white">
          <div className="w-12 h-12 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin mb-3"></div>
          <div>Yükleniyor...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 pb-6 border-b border-gray-700">
          <div>
            <h1 className="text-3xl font-bold">Hoş Geldiniz, {user?.username}!</h1>
            <p className="text-gray-400">Zaman çizelgelerinizi buradan yönetebilirsiniz</p>
          </div>
          <div className="flex gap-3 w-full md:w-auto">
            {user?.isSuperAdmin && (
              <button
                onClick={() => navigate('/yonetim')}
                className="bg-yellow-600 hover:bg-yellow-700 px-4 py-2 rounded-lg shadow-md transition duration-150 flex items-center justify-center flex-1 md:flex-none"
              >
                Yönetim
              </button>
            )}
            <button
              onClick={() => navigate('/hesabim')}
              className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded-lg shadow-md transition duration-150 flex items-center justify-center flex-1 md:flex-none"
            >
              Hesabım
            </button>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg shadow-md transition duration-150 flex items-center justify-center flex-1 md:flex-none"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              Zaman Çizelgesi Oluştur
            </button>
            <button
              onClick={logout}
              className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg shadow-md transition duration-150 flex items-center justify-center flex-1 md:flex-none"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
              </svg>
              Çıkış Yap
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/90 text-white p-4 rounded-lg mb-6 font-medium shadow-md">
            {error}
          </div>
        )}

        {showCreateForm && (
          <div className="bg-gray-800/90 backdrop-blur-sm p-6 rounded-xl mb-8 shadow-lg border border-gray-700">
            <h2 className="text-xl font-bold mb-6 pb-2 border-b border-gray-700">Yeni Zaman Çizelgesi Oluştur</h2>
            <form onSubmit={handleCreateTimeline}>
              <div className="mb-5">
                <label className="block mb-2 font-medium">Başlık</label>
                <input
                  type="text"
                  value={newTimeline.title}
                  onChange={(e) => setNewTimeline({ ...newTimeline, title: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-700/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                  required
                />
              </div>

              <div className="mb-5">
                <label className="block mb-2 font-medium">Açıklama</label>
                <textarea
                  value={newTimeline.description}
                  onChange={(e) => setNewTimeline({ ...newTimeline, description: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-700/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                  rows="3"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
                <div className="p-4 bg-gray-700/40 rounded-lg">
                  <label className="block mb-3 font-medium">Başlangıç Tarihi <span className="text-red-500">*</span></label>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-sm text-gray-300 mb-1 block">Yıl</label>
                      <input
                        type="number"
                        value={newTimeline.startDate.year}
                        onChange={(e) => setNewTimeline({
                          ...newTimeline,
                          startDate: {
                            ...newTimeline.startDate,
                            year: e.target.value === '' ? '' : Math.abs(parseInt(e.target.value, 10))
                          }
                        })}
                        className="w-full px-3 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-300 mb-1 block">Ay</label>
                      <select
                        value={newTimeline.startDate.month}
                        onChange={(e) => setNewTimeline({
                          ...newTimeline,
                          startDate: {
                            ...newTimeline.startDate,
                            month: parseInt(e.target.value)
                          }
                        })}
                        className="w-full px-3 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                        required
                      >
                        <option value="" disabled>Ay seçin</option>
                        {Array.from({ length: 12 }, (_, i) => (
                          <option key={i} value={i}>
                            {new Date(2000, i, 1).toLocaleString('tr-TR', { month: 'long' })}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-gray-300 mb-1 block">Gün</label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={newTimeline.startDate.day}
                        onChange={(e) => setNewTimeline({
                          ...newTimeline,
                          startDate: {
                            ...newTimeline.startDate,
                            day: parseInt(e.target.value)
                          }
                        })}
                        className="w-full px-3 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                        required
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    {/* MÖ checkbox hidden per request - functionality still works with negative year values */}
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={newTimeline.startDate.isBC}
                        onChange={(e) => setNewTimeline({
                          ...newTimeline,
                          startDate: {
                            ...newTimeline.startDate,
                            isBC: e.target.checked
                          }
                        })}
                        className="form-checkbox h-5 w-5 text-blue-600 rounded border-gray-600 bg-gray-700"
                      />
                      <span className="ml-2">MÖ</span>
                    </label>
                  </div>
                </div>

                <div className="p-4 bg-gray-700/40 rounded-lg">
                  <label className="block mb-3 font-medium">Bitiş Tarihi <span className="text-red-500">*</span></label>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-sm text-gray-300 mb-1 block">Yıl</label>
                      <input
                        type="number"
                        value={newTimeline.endDate.year}
                        onChange={(e) => setNewTimeline({
                          ...newTimeline,
                          endDate: {
                            ...newTimeline.endDate,
                            year: e.target.value === '' ? '' : Math.abs(parseInt(e.target.value, 10))
                          }
                        })}
                        className="w-full px-3 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-300 mb-1 block">Ay</label>
                      <select
                        value={newTimeline.endDate.month}
                        onChange={(e) => setNewTimeline({
                          ...newTimeline,
                          endDate: {
                            ...newTimeline.endDate,
                            month: parseInt(e.target.value)
                          }
                        })}
                        className="w-full px-3 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                        required
                      >
                        <option value="" disabled>Ay seçin</option>
                        {Array.from({ length: 12 }, (_, i) => (
                          <option key={i} value={i}>
                            {new Date(2000, i, 1).toLocaleString('tr-TR', { month: 'long' })}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm text-gray-300 mb-1 block">Gün</label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={newTimeline.endDate.day}
                        onChange={(e) => setNewTimeline({
                          ...newTimeline,
                          endDate: {
                            ...newTimeline.endDate,
                            day: parseInt(e.target.value)
                          }
                        })}
                        className="w-full px-3 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                        required
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    {/* MÖ checkbox hidden per request - functionality still works with negative year values */}
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={newTimeline.endDate.isBC}
                        onChange={(e) => setNewTimeline({
                          ...newTimeline,
                          endDate: {
                            ...newTimeline.endDate,
                            isBC: e.target.checked
                          }
                        })}
                        className="form-checkbox h-5 w-5 text-blue-600 rounded border-gray-600 bg-gray-700"
                      />
                      <span className="ml-2">MÖ</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
                <div>
                  <label className="block mb-2 font-medium">Arka Plan Rengi</label>
                  <div className="flex items-center">
                    <input
                      type="color"
                      value={newTimeline.backgroundColor}
                      onChange={(e) => setNewTimeline({ ...newTimeline, backgroundColor: e.target.value })}
                      className="h-10 w-12 rounded-l-lg border-0"
                    />
                    <input
                      type="text"
                      value={newTimeline.backgroundColor}
                      onChange={(e) => setNewTimeline({ ...newTimeline, backgroundColor: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-2 font-medium">Arka Plan Resmi URL</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newTimeline.backgroundImage}
                      onChange={(e) => setNewTimeline({ ...newTimeline, backgroundImage: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                      placeholder="https://ornek.com/resim.jpg"
                    />
                    <label className="bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded-lg shadow-md transition cursor-pointer flex items-center">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleFileUpload('backgroundImage')}
                      />
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block mb-2 font-medium">Kapak Resmi URL</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newTimeline.coverImage}
                      onChange={(e) => setNewTimeline({ ...newTimeline, coverImage: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                      placeholder="https://ornek.com/resim.jpg"
                    />
                    <label className="bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded-lg shadow-md transition cursor-pointer flex items-center">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleFileUpload('coverImage')}
                      />
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block mb-2 font-medium">Logo URL</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newTimeline.logoUrl}
                      onChange={(e) => setNewTimeline({ ...newTimeline, logoUrl: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                      placeholder="/trbayrak.png"
                    />
                    <label className="bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded-lg shadow-md transition cursor-pointer flex items-center">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleFileUpload('logoUrl')}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block mb-2 font-medium">Başlangıç Noktası</label>
                <select
                  value={newTimeline.beginAt}
                  onChange={(e) => setNewTimeline({ ...newTimeline, beginAt: e.target.value })}
                  className="w-full px-3 py-3 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                >
                  <option value="firstStory">İlk Olay</option>
                  <option value="lastStory">Son Olay</option>
                  <option value="currentDate">Güncel Tarih</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block mb-2 font-medium">Görünürlük</label>
                <div className="bg-gray-700/70 rounded-lg p-3 border border-gray-600">
                  <label htmlFor="isPublicToggle" className="flex items-center cursor-pointer relative">
                    <input
                      type="checkbox"
                      id="isPublicToggle"
                      checked={newTimeline.isPublic}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setShowPublicWarningModal(true);
                        } else {
                          setNewTimeline({ ...newTimeline, isPublic: false });
                        }
                      }}
                      className="sr-only"
                    />
                    <div className="toggle-bg bg-gray-600 border-2 border-gray-500 h-6 w-11 rounded-full"></div>
                    <span className={`ml-3 text-white transition-opacity duration-200 ${newTimeline.isPublic ? 'opacity-100' : 'opacity-70'}`}>
                      Zaman çizelgesi herkese açık
                    </span>
                  </label>
                </div>
                <p className="text-sm text-gray-400 mt-2">
                  Herkese açık zaman çizelgeleri, ana sayfadaki zaman çizelgeleri listesinde görünecektir.
                </p>
              </div>

              {/* Uyarı Alanı */}
              <div className="mb-6 p-4 bg-yellow-900/30 border border-yellow-600/50 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div>
                    <p className="text-yellow-400 font-semibold text-sm mb-1">Önemli Uyarı</p>
                    <p className="text-yellow-200/80 text-xs leading-relaxed">
                      Zaman çizelgesinde toplum kurallarına ve hukuka aykırı, yasa dışı içerik oluşturmak kesinlikle yasaktır. 
                      Her yayıncı, paylaştığı içerikten bizzat sorumludur.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg shadow-md transition duration-150 font-medium flex items-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Oluştur
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg shadow-md transition duration-150 font-medium"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {timelines.map((timeline) => (
            <div
              key={timeline._id}
              className="bg-gray-800/80 backdrop-blur-sm rounded-xl p-6 hover:bg-gray-800 transition-colors shadow-lg border border-gray-700 flex flex-col"
            >
              <h3 className="text-xl font-bold mb-2">{timeline.title}</h3>
              <p className="text-gray-300 mb-6 flex-grow">{timeline.intro || timeline.description}</p>
              <div className="flex gap-3 mt-auto">
                <button
                  onClick={() => navigate(`/cizelge/${timelineService.getTimelineUrlIdentifier(timeline)}`)}
                  className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg flex-1 shadow-md transition duration-150 flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                  Görüntüle
                </button>
                <button
                  onClick={() => handleDeleteTimeline(timeline._id)}
                  className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg shadow-md transition duration-150 flex items-center justify-center"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {timelines.length === 0 && (
          <div className="text-center text-gray-300 mt-16 p-12 bg-gray-800/60 backdrop-blur-sm rounded-xl border border-gray-700">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
            <p className="text-xl mb-4">Henüz hiç zaman çizelgesi oluşturmadınız.</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="text-blue-400 hover:text-blue-300 font-medium bg-blue-500/10 hover:bg-blue-500/20 px-6 py-3 rounded-lg transition-colors inline-flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              İlk zaman çizelgenizi oluşturun
            </button>
          </div>
        )}
      </div>

      {/* Herkese Açık Uyarı Modalı */}
      {showPublicWarningModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full p-6 border border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-yellow-500/20 rounded-full">
                <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white">İçerik Sorumluluğu</h3>
            </div>
            <div className="mb-6 text-gray-300 text-sm leading-relaxed space-y-3">
              <p>
                Zaman çizelgenizi herkese açık hale getirmeden önce lütfen aşağıdaki kuralları okuyun ve kabul edin:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>Toplum kurallarına ve hukuka aykırı, yasa dışı içerik oluşturmak <strong className="text-red-400">kesinlikle yasaktır</strong>.</li>
                <li>Her yayıncı, paylaştığı içerikten <strong className="text-yellow-400">bizzat sorumludur</strong>.</li>
                <li>Nefret söylemi, şiddet, iftira veya telif hakkı ihlali içeren içerikler kaldırılır ve hesap askıya alınabilir.</li>
                <li>Yönetim, uygunsuz içerikleri önceden haber vermeksizin kaldırma hakkını saklı tutar.</li>
              </ul>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setNewTimeline({ ...newTimeline, isPublic: true });
                  setShowPublicWarningModal(false);
                }}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg font-medium transition"
              >
                Okudum, Kabul Ediyorum
              </button>
              <button
                onClick={() => setShowPublicWarningModal(false)}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2.5 rounded-lg font-medium transition"
              >
                Vazgeç
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 